/**
 * ConversationStateMachine.js
 * 对话流式状态机 - 管理八种对话状态及多设备并发仲裁
 */

// 八种对话状态
export const CONVERSATION_STATES = {
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  ONBOARDING: 'onboarding',
  IDLE: 'idle',
  LISTENING: 'listening',
  THINKING: 'thinking',
  ANSWERING: 'answering',
  RENDERING: 'rendering',
};

// 合法的状态转换映射
const VALID_TRANSITIONS = {
  [CONVERSATION_STATES.OFFLINE]: [CONVERSATION_STATES.CONNECTING],
  [CONVERSATION_STATES.CONNECTING]: [CONVERSATION_STATES.ONBOARDING, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.ONBOARDING]: [CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.IDLE]: [CONVERSATION_STATES.LISTENING, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.LISTENING]: [CONVERSATION_STATES.THINKING, CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.THINKING]: [CONVERSATION_STATES.ANSWERING, CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.ANSWERING]: [CONVERSATION_STATES.RENDERING, CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE],
  [CONVERSATION_STATES.RENDERING]: [CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE],
};

/**
 * 多设备并发仲裁器
 * 当多个设备同时尝试进入 LISTENING 时，按优先级仲裁
 */
class DeviceArbiter {
  constructor() {
    this.activeDevice = null;
    this.pendingDevices = [];
    this.devicePriorities = new Map(); // deviceId -> priority (数值越小优先级越高)
  }

  /**
   * 注册设备优先级
   */
  registerDevice(deviceId, priority = 10) {
    this.devicePriorities.set(deviceId, priority);
  }

  /**
   * 请求获得对话控制权
   * @returns {boolean} 是否获得控制权
   */
  requestControl(deviceId) {
    if (!this.activeDevice) {
      this.activeDevice = deviceId;
      return true;
    }
    const currentPriority = this.devicePriorities.get(this.activeDevice) ?? 10;
    const requestPriority = this.devicePriorities.get(deviceId) ?? 10;
    if (requestPriority < currentPriority) {
      this.pendingDevices.push(this.activeDevice);
      this.activeDevice = deviceId;
      return true;
    }
    this.pendingDevices.push(deviceId);
    return false;
  }

  /**
   * 释放对话控制权
   */
  releaseControl(deviceId) {
    if (this.activeDevice === deviceId) {
      // 按优先级选出下一个设备
      if (this.pendingDevices.length > 0) {
        this.pendingDevices.sort((a, b) =>
          (this.devicePriorities.get(a) ?? 10) - (this.devicePriorities.get(b) ?? 10)
        );
        this.activeDevice = this.pendingDevices.shift();
      } else {
        this.activeDevice = null;
      }
    } else {
      this.pendingDevices = this.pendingDevices.filter(d => d !== deviceId);
    }
  }

  getActiveDevice() {
    return this.activeDevice;
  }

  isActive(deviceId) {
    return this.activeDevice === deviceId;
  }
}

/**
 * ConversationStateMachine
 * 单例模式对话状态机，支持监听器、多设备仲裁、唤醒闭环
 */
class ConversationStateMachine {
  constructor() {
    this.state = CONVERSATION_STATES.OFFLINE;
    this.previousState = null;
    this.listeners = new Map(); // event -> [callbacks]
    this.arbiter = new DeviceArbiter();
    this.currentDeviceId = null;
    this.wakeWordPending = false; // 唤醒闭环标志
    this.stateHistory = [];
    this.maxHistoryLength = 50;
  }

  // ── 事件系统 ──────────────────────────────────────────

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, []);
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback); // 返回取消订阅函数
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    this.listeners.set(event, this.listeners.get(event).filter(cb => cb !== callback));
  }

  emit(event, payload) {
    const cbs = this.listeners.get(event) || [];
    cbs.forEach(cb => {
      try { cb(payload); } catch (e) { console.error(`[CSM] listener error on "${event}":`, e); }
    });
  }

  // ── 核心状态转换 ──────────────────────────────────────

  /**
   * 尝试转换到目标状态
   * @param {string} targetState
   * @param {object} meta - 携带的元数据 (deviceId, reason 等)
   * @returns {boolean}
   */
  transition(targetState, meta = {}) {
    const allowed = VALID_TRANSITIONS[this.state] || [];
    if (!allowed.includes(targetState)) {
      console.warn(`[CSM] 非法转换: ${this.state} → ${targetState}`);
      this.emit('invalidTransition', { from: this.state, to: targetState, meta });
      return false;
    }

    // 多设备仲裁：进入 LISTENING 需要获得控制权
    if (targetState === CONVERSATION_STATES.LISTENING) {
      const deviceId = meta.deviceId || 'default';
      if (!this.arbiter.requestControl(deviceId)) {
        console.warn(`[CSM] 设备 ${deviceId} 未获得仲裁控制权`);
        this.emit('arbitrationDenied', { deviceId, activeDevice: this.arbiter.getActiveDevice() });
        return false;
      }
      this.currentDeviceId = deviceId;
    }

    // 释放控制权：离开 LISTENING/THINKING/ANSWERING/RENDERING 回到 IDLE/OFFLINE
    if (
      this.currentDeviceId &&
      [CONVERSATION_STATES.IDLE, CONVERSATION_STATES.OFFLINE].includes(targetState) &&
      [CONVERSATION_STATES.LISTENING, CONVERSATION_STATES.THINKING,
       CONVERSATION_STATES.ANSWERING, CONVERSATION_STATES.RENDERING].includes(this.state)
    ) {
      this.arbiter.releaseControl(this.currentDeviceId);
      this.currentDeviceId = null;
    }

    this.previousState = this.state;
    this.state = targetState;

    // 记录历史
    this.stateHistory.push({ state: targetState, ts: Date.now(), meta });
    if (this.stateHistory.length > this.maxHistoryLength) this.stateHistory.shift();

    this.emit('stateChange', { from: this.previousState, to: this.state, meta });
    this.emit(`enter:${targetState}`, { from: this.previousState, meta });
    this.emit(`leave:${this.previousState}`, { to: this.state, meta });

    // 唤醒闭环：从 RENDERING → IDLE 时，若有待处理的唤醒词，自动进入 LISTENING
    if (targetState === CONVERSATION_STATES.IDLE && this.wakeWordPending) {
      this.wakeWordPending = false;
      setTimeout(() => {
        this.emit('wakeWordLoopback', {});
        this.transition(CONVERSATION_STATES.LISTENING, { deviceId: meta.deviceId || 'default', reason: 'wakeWordLoopback' });
      }, 0);
    }

    return true;
  }

  // ── 便捷方法 ──────────────────────────────────────────

  connect() { return this.transition(CONVERSATION_STATES.CONNECTING); }
  onboard() { return this.transition(CONVERSATION_STATES.ONBOARDING); }
  ready()   { return this.transition(CONVERSATION_STATES.IDLE); }

  startListening(deviceId = 'default') {
    return this.transition(CONVERSATION_STATES.LISTENING, { deviceId });
  }
  startThinking() { return this.transition(CONVERSATION_STATES.THINKING); }
  startAnswering() { return this.transition(CONVERSATION_STATES.ANSWERING); }
  startRendering() { return this.transition(CONVERSATION_STATES.RENDERING); }
  backToIdle(meta = {}) { return this.transition(CONVERSATION_STATES.IDLE, meta); }
  goOffline() { return this.transition(CONVERSATION_STATES.OFFLINE); }

  /**
   * 触发唤醒词 - 若当前非 IDLE，标记待处理，实现闭环
   */
  triggerWakeWord(deviceId = 'default') {
    if (this.state === CONVERSATION_STATES.IDLE) {
      return this.startListening(deviceId);
    }
    this.wakeWordPending = true;
    this.emit('wakeWordQueued', { deviceId, currentState: this.state });
    return false;
  }

  // ── 设备仲裁 API ──────────────────────────────────────

  registerDevice(deviceId, priority) {
    this.arbiter.registerDevice(deviceId, priority);
  }

  getActiveDevice() {
    return this.arbiter.getActiveDevice();
  }

  // ── 查询 ──────────────────────────────────────────────

  getState() { return this.state; }
  getPreviousState() { return this.previousState; }
  getHistory() { return [...this.stateHistory]; }

  is(state) { return this.state === state; }
  isIdle() { return this.state === CONVERSATION_STATES.IDLE; }
  isListening() { return this.state === CONVERSATION_STATES.LISTENING; }
  isThinking() { return this.state === CONVERSATION_STATES.THINKING; }
  isAnswering() { return this.state === CONVERSATION_STATES.ANSWERING; }
  isRendering() { return this.state === CONVERSATION_STATES.RENDERING; }
  isOffline() { return this.state === CONVERSATION_STATES.OFFLINE; }

  /**
   * 重置状态机（用于测试或重新初始化）
   */
  reset() {
    this.state = CONVERSATION_STATES.OFFLINE;
    this.previousState = null;
    this.currentDeviceId = null;
    this.wakeWordPending = false;
    this.stateHistory = [];
    this.arbiter = new DeviceArbiter();
    this.emit('reset', {});
  }
}

// 导出单例
export const conversationStateMachine = new ConversationStateMachine();
export default ConversationStateMachine;
